import { MetricToImperialConverter } from '../converters';
import { Gender } from '../general';
import { Allergens } from './ingredient';
import { Meal } from './meal';
import { MealIngredient } from './mealingredient';
import { ShoppingListExclusion } from './shoppingListExclusion';

export enum DietGoal {
    BurnFat = 'BurnFat',
    BuildMuscle = 'BuildMuscle',
    BurnFatAndBuildMuscle = 'BurnFatAndBuildMuscle',
}

export enum FoodPreference {
    Vegetarian = 'Vegetarian',
    Vegan = 'Vegan',
    HighProtein = 'HighProtein',
    AllFoodGroups = 'AllFoodGroups',
}

export enum MealOfDay {
    BreakfastNextDay = 'BreakfastNextDay',
    Breakfast = 'Breakfast',
    MorningSnack = 'MorningSnack',
    Lunch = 'Lunch',
    AfternoonSnack = 'AfternoonSnack',
    Dinner = 'Dinner',
    Snack = 'Snack',
}

export const MealsOfDayInOrder = [
    MealOfDay.Breakfast,
    MealOfDay.MorningSnack,
    MealOfDay.Lunch,
    MealOfDay.AfternoonSnack,
    MealOfDay.Dinner,
    MealOfDay.Snack,
    MealOfDay.BreakfastNextDay,
];

export enum DayOfWeek {
    Monday = 'Monday',
    Tuesday = 'Tuesday',
    Wednesday = 'Wednesday',
    Thursday = 'Thursday',
    Friday = 'Friday',
    Saturday = 'Saturday',
    Sunday = 'Sunday',
}

export const MondayToSunday: DayOfWeek[] = [
  DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday,
  DayOfWeek.Saturday, DayOfWeek.Sunday,
];

// Will is used to store in Couchdb DB
// Prepopulate meal plans, no need to let user edit
export class MealPlanData {
    public static GenerateId(displayName: string): string {
        return displayName;
    }
    public static GenerateIdFromPlan(mealPlanData: MealPlanData): string {
        const organisationStr = mealPlanData.organisation ? '_' + mealPlanData.organisation: '';
        return mealPlanData.dietGoal + '_' + mealPlanData.gender + '_' +
            mealPlanData.foodPreference + organisationStr;
    }
    // tslint:disable-next-line:variable-name
    public _id: string;
    public displayName: string;
    public dietGoal: DietGoal;
    public foodPreference: FoodPreference;
    public gender: Gender;
    public mealIdSchedule: Map<DayOfWeek, Map<MealOfDay, string>>;
    public shoppingListExclusions: ShoppingListExclusion[];
    public checklistIngredientIds: string[];
    public organisation?: string;
    public shoppingListServingCount?: number | undefined; // optional for backwards compatability
}
// stored on the user profile
// tslint:disable-next-line:max-classes-per-file
export class MealPreferences {
    public static hasMealPreferenceChanged(a: MealPreferences, b: MealPreferences) {
        return a.foodPreference !== b.foodPreference || a.dietGoal !== b.dietGoal;
    }

    public dietGoal: DietGoal;
    public foodPreference: FoodPreference;
}

// Use this class in code, since it combines meals and plans and has util methods.
// tslint:disable-next-line:max-classes-per-file
export class MealPlanWithMeals extends MealPlanData {
    public static amountOfDietDaysPerWeek = 7;
    public static amountOfmealsPerDay = 5;
    public static disableImperialOnMacros = true;
    // need to pass all meals that could be in this plan
    public static Create(
        mealPlan: MealPlanData,
        allMeals: Meal[],
        allIngredients: MealIngredient[],
        allergens: Allergens[],
        isUserHighlyActive?: boolean,
        useImperialUnit = false,
      ): MealPlanWithMeals {
        const shouldReducePortionSize = !isUserHighlyActive;
        const newObj: MealPlanWithMeals = Object.assign(new MealPlanWithMeals(), mealPlan);
        MealPlanWithMeals.ValidateMealPlan(mealPlan);
        MealPlanWithMeals.PopulateMeals(
          newObj,
          allMeals,
          allIngredients,
          useImperialUnit,
          allergens,
          shouldReducePortionSize,
        );
        MealPlanWithMeals.PopulateIngredientProperties(newObj, allIngredients);
        MealPlanWithMeals.PopulateCaloriesFromOldIngredients(newObj);
        if (useImperialUnit) {
            MetricToImperialConverter.convertMealsIngredientsDescriptionImperial(newObj, allIngredients, allergens);
            if (!this.disableImperialOnMacros) {
                MetricToImperialConverter.convertDailyMealMacroCountToImperial(newObj);
                MetricToImperialConverter.convertIndividuLMealMacroCountToImperial(newObj, allIngredients);
            }
        }
        return newObj;
    }

    private static PopulateCaloriesFromOldIngredients(mealPlanWithMeals: MealPlanWithMeals) {
        mealPlanWithMeals.caloriesForDay = new Map<DayOfWeek, number>();
        // tslint:disable-next-line:forin
        for (const dayOfWeek in DayOfWeek) {
            const dayPlan: Map<MealOfDay, Meal> | undefined
                = mealPlanWithMeals.mealSchedule.get(DayOfWeek[String(dayOfWeek)]);
            if (dayPlan) {
                const dayMeals = [ ...dayPlan.values() ];
                const dayCalories = dayMeals.reduce((prev, next) => prev + Meal.OldIngredientCalories(next), 0);
                mealPlanWithMeals.caloriesForDay[dayOfWeek] = dayCalories;
            }
        }
    }

    private static PopulateIngredientProperties(
        mealPlanWithMeals: MealPlanWithMeals,
        allIngredients: MealIngredient[]) {
        mealPlanWithMeals.caloriesForDayNewIngredientsModel = new Map<DayOfWeek, number>();
        mealPlanWithMeals.fatForDay = new Map<DayOfWeek, number>();
        mealPlanWithMeals.carbsForDay = new Map<DayOfWeek, number>();
        mealPlanWithMeals.proteinForDay = new Map<DayOfWeek, number>();
        // tslint:disable-next-line:forin
        for (const dayOfWeek in DayOfWeek) {
            const dayPlan: Map<MealOfDay, Meal> | undefined
                = mealPlanWithMeals.mealSchedule.get(DayOfWeek[String(dayOfWeek)]);
            if (dayPlan) {
                const dayMeals = [ ...dayPlan.values() ];
                const dayCalories = dayMeals.reduce((prev, next) => {
                    const calories = Meal.Calories(next, allIngredients);
                    next.calories = calories;
                    return prev + calories;
                }, 0);
                const dayFat = dayMeals.reduce((prev, next) => {
                    const fat = Meal.Fat(next, allIngredients);
                    next.fat = fat;
                    return prev + fat;
                }, 0);
                const dayCarbs = dayMeals.reduce((prev, next) => {
                    const carbs = Meal.Carbs(next, allIngredients);
                    next.carbs = carbs;
                    return prev + carbs;
                }, 0);
                const dayProtein = dayMeals.reduce((prev, next) => {
                    const protein = Meal.Protein(next, allIngredients);
                    next.protein = protein;
                    return prev + protein;
                }, 0);
                mealPlanWithMeals.fatForDay.set(DayOfWeek[String(dayOfWeek)], dayFat);
                mealPlanWithMeals.caloriesForDayNewIngredientsModel[dayOfWeek] = dayCalories;
                mealPlanWithMeals.carbsForDay.set(DayOfWeek[String(dayOfWeek)], dayCarbs);
                mealPlanWithMeals.proteinForDay.set(DayOfWeek[String(dayOfWeek)], dayProtein);
            }
        }
    }

    private static ValidateMealPlan(mealPlan: MealPlanData) {
        if (mealPlan.mealIdSchedule && mealPlan.mealIdSchedule.size === MealPlanWithMeals.amountOfDietDaysPerWeek) {
            for (const dayOfWeek of mealPlan.mealIdSchedule.keys()) {
                const mealPlanForDay = mealPlan.mealIdSchedule.get(dayOfWeek);
                if (mealPlanForDay && mealPlanForDay.size === MealPlanWithMeals.amountOfmealsPerDay) {
                    return;
                }
            }
        }
        throw Error(`Not all the required meals are set for meal plan ${mealPlan.displayName}.
        There should be ${MealPlanWithMeals.amountOfDietDaysPerWeek} days of meals,
        ${MealPlanWithMeals.amountOfmealsPerDay} meals per day.`);
    }

    private static PopulateMeals(
        newObj: MealPlanWithMeals,
        allMeals: Meal[],
        allMealIngredients: MealIngredient[],
        useImperialUnit = false,
        allergens: Allergens[],
        shouldReducePortionSize: boolean,
      ) {
        newObj.mealSchedule = new Map<DayOfWeek, Map<MealOfDay, Meal>>();
        for (const day of newObj.mealIdSchedule.keys()) {
            const dayPlan = newObj.mealIdSchedule.get(day);
            if (dayPlan) {
                for (const mealOfDay of dayPlan.keys()) {
                    const mealId = dayPlan.get(mealOfDay);
                    const meal = allMeals.find((m) => m._id === mealId);
                    if (meal instanceof Meal) {
                        // clone to ensure properties are set for individual instances
                        const clone = new Meal();
                        Object.assign(clone, meal);
                        if (shouldReducePortionSize) {
                            Meal.setLowerCaloricMealQuantities(clone, allMealIngredients);
                        }
                        Meal.setImageUrls(clone);
                        Meal.setIngredientDescriptions(clone, allMealIngredients, allergens, useImperialUnit);
                        MealPlanWithMeals.setIsInShoppingList(clone, newObj.shoppingListExclusions, day, mealOfDay);
                        let mealPlanForDay = newObj.mealSchedule.get(day);
                        if (mealPlanForDay) {
                            mealPlanForDay.set(mealOfDay, clone);
                        } else {
                            mealPlanForDay = new Map([[mealOfDay, clone]]);
                        }
                        newObj.mealSchedule.set(day, mealPlanForDay);
                    } else {
                        throw Error(`No meal found with id ${mealId}. Could not build up MealPlanWithMeals.`);
                    }
                }
            }
        }
    }

    private static setIsInShoppingList(meal: Meal,
                                       shoppingExclusions: ShoppingListExclusion[] | undefined,
                                       day: DayOfWeek,
                                       mealOfDay: MealOfDay) {
        meal.isInShoppingList = true;
        if (shoppingExclusions) {
            const shoppingExclusionForMeal = shoppingExclusions.find(
                (se) => se.dayOfWeek === day && se.timeOfDay === mealOfDay);
            if (shoppingExclusionForMeal) {
                meal.isInShoppingList = false;
            }
        }
    }

    public caloriesForDayNewIngredientsModel: Map<DayOfWeek, number>;
    public caloriesForDay: Map<DayOfWeek, number>;
    public fatForDay: Map<DayOfWeek, number>;
    public carbsForDay: Map<DayOfWeek, number>;
    public proteinForDay: Map<DayOfWeek, number>;
    public mealSchedule: Map<DayOfWeek, Map<MealOfDay, Meal>>;
}

export interface IMealPlanImportData {
  _id: string;
  displayName: string;
  dietGoal: DietGoal;
  foodPreference: FoodPreference;
  gender: Gender;
  mealIdSchedule: { [key: string]: { [key: string]: string } };
  organisation?: string;
}
